// API Configuration for Nutrition Intelligence Platform
// Version: 1.0.2 - Explicit environment configuration
//
// REACT_APP_API_BASE_URL values:
// - Development: 'http://localhost:8000' (set in .env.development or package.json)
// - Production: '' (empty string - set in Dockerfile, uses relative URLs via Nginx proxy)
//
// Empty string means the frontend makes requests to '/api/v1/...' which Nginx
// proxies to the backend container

import axios from 'axios';
import { isGuest, blockGuestWrite, guestBlockedError } from '../services/guestMode';

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL !== undefined
  ? process.env.REACT_APP_API_BASE_URL
  : (process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '');

// Send HttpOnly auth cookies on every axios request by default.
// The backend emits `ni_access` and `ni_refresh` cookies on login;
// without `withCredentials` they would not be attached to subsequent
// requests and the cookie auth path would never trigger.
axios.defaults.withCredentials = true;

// Guest mode: block API writes and offer login instead. One interceptor
// covers every service — no per-button guards needed. Calculation-only
// POSTs (pure functions, nothing persisted) stay allowed, and /auth must
// work so the guest can actually leave guest mode.
const GUEST_ALLOWED_WRITES = [
  '/auth/',
  '/nutrition-calculator/',
  '/equivalences/calculate-equivalents',
];
axios.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (!isGuest() || !['post', 'put', 'patch', 'delete'].includes(method)) return config;
  const url = config.url || '';
  const isApi = url.startsWith('/api/') || url.includes(`${API_BASE_URL}/api/`);
  if (!isApi || GUEST_ALLOWED_WRITES.some((p) => url.includes(p))) return config;
  blockGuestWrite();
  return Promise.reject(guestBlockedError());
});

// Global 401 recovery for ALL axios-based services (food log, Huitzi,
// medicinal plants, etc.). The access token expires after ~30 min; on a
// 401 we refresh once via the ni_refresh cookie and replay the request.
// Without this, every axios call after expiry failed silently mid-session.
let _axiosRefreshing = null;
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response && error.response.status;
    const isRefreshCall = original && original.url && original.url.includes('/auth/refresh');
    // A guest has no session to refresh — fail fast instead of a doomed
    // refresh round-trip on every protected endpoint the UI touches.
    if (status === 401 && original && !original._retried && !isRefreshCall && !isGuest()) {
      original._retried = true;
      try {
        if (!_axiosRefreshing) {
          _axiosRefreshing = axios.post(
            `${API_BASE_URL}/api/v1/auth/refresh`, {}, { withCredentials: true }
          ).finally(() => { _axiosRefreshing = null; });
        }
        const r = await _axiosRefreshing;
        const newToken = r && r.data && r.data.access_token;
        if (newToken) {
          try {
            localStorage.setItem('nutrition_access_token', newToken);
            if (r.data.refresh_token) localStorage.setItem('nutrition_refresh_token', r.data.refresh_token);
          } catch (_) { /* private mode */ }
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${newToken}`;
        }
        return axios(original);
      } catch (_) {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// API Endpoints
export const API_ENDPOINTS = {
  // Health & System
  HEALTH: `${API_BASE_URL}/health`,

  // Core Data
  FOODS: `${API_BASE_URL}/api/v1/foods`,
  RECIPES: `${API_BASE_URL}/api/v1/recipes`,
  EQUIVALENCES: `${API_BASE_URL}/api/v1/equivalences`,
  PATIENTS: `${API_BASE_URL}/api/v1/patients`,

  // Calculations & Planning
  NUTRITION_CALCULATOR: `${API_BASE_URL}/api/v1/nutrition-calculator`,
  MEAL_PLANS: `${API_BASE_URL}/api/v1/meal-plans`,
  WEEKLY_PLANNING: `${API_BASE_URL}/api/v1/weekly-planning`,

  // AI Services
  VISION: `${API_BASE_URL}/api/v1/vision`,

  // Authentication
  AUTH: `${API_BASE_URL}/api/v1/auth`,

  // Documentation
  DOCS: `${API_BASE_URL}/docs`,
  REDOC: `${API_BASE_URL}/redoc`,
};

// Development helper - log API configuration in console
if (process.env.NODE_ENV === 'development') {
  console.log('🌐 API Configuration:', {
    baseUrl: API_BASE_URL,
    environment: process.env.NODE_ENV,
    fromEnvVar: !!process.env.REACT_APP_API_BASE_URL
  });
}

export default {
  API_BASE_URL,
  API_ENDPOINTS
};